import React, { useState } from "react";
import { Link } from "react-router-dom";
import { caseStudies, moreProjects } from "../data/mock";
import { ArrowUpRight, Plus, Check } from "lucide-react";

const CaseStudies = () => {
  const [showMore, setShowMore] = useState(false);

  return (
    <section id="work" className="pt-32 lg:pt-40 pb-24 lg:pb-32 bg-neutral-50/40 dark:bg-neutral-900/40">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="max-w-[820px]">
          <h2 className="text-[34px] sm:text-[44px] lg:text-[56px] leading-[1.05] tracking-[-0.03em] font-medium text-neutral-900 dark:text-neutral-50">
            What I’ve been{" "}
            <span style={{ fontFamily: "'Segoe Script', 'Caveat', cursive" }} className="font-normal text-neutral-600 dark:text-neutral-400">
              designing.
            </span>
          </h2>
          <p className="mt-4 text-[16px] sm:text-[17px] text-neutral-600 dark:text-neutral-400 max-w-[560px] leading-[1.6]">
            Let’s go through some of my works — the challenge, the approach, the outcome.
          </p>
        </div>

        {/* Cards — first 4 always visible, the rest revealed via "Load more projects" */}
        <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-5 lg:gap-6">
          {caseStudies
            .slice(0, showMore ? caseStudies.length : 4)
            .map((cs, idx) => (
              <CaseStudyCard key={cs.id} cs={cs} idx={idx} />
            ))}
        </div>

        {/* Legacy "more projects" grid (kept for compatibility, hidden when moreProjects is empty) */}
        {showMore && moreProjects.length > 0 && (
          <div className="mt-5 lg:mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {moreProjects.map((p, idx) => (
              <MoreProjectCard key={p.id} p={p} idx={idx + caseStudies.length} />
            ))}
          </div>
        )}

        {/* Load more — shown when there are more case studies than the first 4 */}
        {caseStudies.length > 4 && (
          <div className="mt-12 flex flex-col items-center gap-3">
            <button
              type="button"
              onClick={() => setShowMore((v) => !v)}
              data-testid="load-more-projects-button"
              className="group inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-950 text-neutral-900 dark:text-neutral-50 hover:border-neutral-300 dark:hover:border-neutral-700 hover:bg-neutral-50 dark:hover:bg-neutral-900 text-[14px] font-medium transition-colors duration-200"
            >
              {showMore ? (
                <>
                  <Check className="w-4 h-4" strokeWidth={2.25} />
                  Showing all projects
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 transition-transform duration-200 group-hover:rotate-90" strokeWidth={2.25} />
                  Load more projects
                </>
              )}
            </button>
            <span className="text-[12px] text-neutral-400 dark:text-neutral-500">
              {showMore
                ? `${caseStudies.length} of ${caseStudies.length} projects`
                : `4 of ${caseStudies.length} projects`}
            </span>
          </div>
        )}
      </div>
    </section>
  );
};

const CaseStudyCard = ({ cs, idx }) => {
  return (
    <Link
      to={`/work/${cs.id}`}
      className="group relative block bg-white dark:bg-neutral-950 border border-neutral-200 dark:border-neutral-800 rounded-2xl overflow-hidden hover:border-neutral-300 dark:hover:border-neutral-700 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_30px_60px_-30px_rgba(0,0,0,0.18)] dark:hover:shadow-[0_30px_60px_-30px_rgba(0,0,0,0.6)]"
    >
      <CaseStudyVisual cs={cs} />

      <div className="p-6 lg:p-7">
        {/* Year + index */}
        <div className="flex items-center justify-between">
          <span className="text-[12px] font-mono uppercase tracking-widest text-neutral-500 dark:text-neutral-400">
            {cs.year}
          </span>
          <span className="text-[12px] font-mono text-neutral-400 dark:text-neutral-500">
            0{idx + 1}
          </span>
        </div>

        {/* Name */}
        <h3 className="mt-3 text-[26px] lg:text-[30px] font-medium tracking-tight text-neutral-900 dark:text-neutral-50 leading-[1.1]">
          {cs.name}
        </h3>

        {/* Tag + button */}
        <div className="mt-7 pt-5 border-t border-neutral-100 dark:border-neutral-800 flex items-center justify-between">
          <span className="inline-flex items-center px-2.5 py-1 rounded-full border border-neutral-200 dark:border-neutral-800 text-[11.5px] uppercase tracking-widest text-neutral-600 dark:text-neutral-400">
            {cs.tag}
          </span>
          <span className="inline-flex items-center gap-1.5 text-[13.5px] font-medium text-neutral-900 dark:text-neutral-50 group-hover:text-neutral-600 dark:group-hover:text-neutral-300 transition-colors duration-200">
            Read case study
            <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2.25} />
          </span>
        </div>
      </div>
    </Link>
  );
};

const MoreProjectCard = ({ p, idx }) => {
  return (
    <article className="group relative bg-white dark:bg-neutral-950 border border-neutral-200 dark:border-neutral-800 rounded-2xl p-6 lg:p-7 hover:border-neutral-300 dark:hover:border-neutral-700 transition-all duration-300 hover:-translate-y-0.5">
      <div className="flex items-center justify-between">
        <span className="text-[12px] font-mono uppercase tracking-widest text-neutral-500 dark:text-neutral-400">
          {p.year}
        </span>
        <span className="text-[12px] font-mono text-neutral-400 dark:text-neutral-500">
          0{idx + 1}
        </span>
      </div>
      <h3 className="mt-3 text-[22px] font-medium tracking-tight text-neutral-900 dark:text-neutral-50 leading-tight">
        {p.name}
      </h3>
      <div className="mt-6 pt-5 border-t border-neutral-100 dark:border-neutral-800 flex items-center justify-between">
        <span className="inline-flex items-center px-2.5 py-1 rounded-full border border-neutral-200 dark:border-neutral-800 text-[11.5px] uppercase tracking-widest text-neutral-600 dark:text-neutral-400">
          {p.tag}
        </span>
        <span className="text-[12.5px] text-neutral-400 dark:text-neutral-500 font-mono">
          Coming soon
        </span>
      </div>
    </article>
  );
};

// ---------- Card visual variants ----------
const IMAGE_POSITION_CLASS = {
  top: "object-top",
  bottom: "object-bottom",
  center: "object-center",
};
const SEVERITY_DOT = {
  high: "bg-rose-500",
  med: "bg-amber-500",
  safe: "bg-emerald-500",
};
const VISUAL_FRAME =
  "h-[220px] bg-gradient-to-br from-neutral-50 to-neutral-100 dark:from-neutral-900 dark:to-neutral-950 border-b border-neutral-200 dark:border-neutral-800 p-6 relative overflow-hidden";
const DOT_GRID =
  "absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,rgba(0,0,0,0.06)_1px,transparent_0)] dark:bg-[radial-gradient(circle_at_1px_1px,rgba(255,255,255,0.06)_1px,transparent_0)] [background-size:18px_18px] opacity-60";

const ImageVisual = ({ image, name, imagePosition }) => {
  const objectPosClass = IMAGE_POSITION_CLASS[imagePosition] || IMAGE_POSITION_CLASS.center;
  return (
    <div className="relative h-[260px] lg:h-[280px] overflow-hidden border-b border-neutral-200 dark:border-neutral-800 bg-neutral-100 dark:bg-neutral-900">
      <img
        src={image}
        alt={`${name} cover`}
        loading="lazy"
        className={`absolute inset-0 w-full h-full object-cover ${objectPosClass} transition-transform duration-700 ease-out group-hover:scale-[1.03]`}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/15 via-transparent to-transparent dark:from-black/40" />
    </div>
  );
};

const SparkMindsVisual = () => (
  <div className={VISUAL_FRAME}>
    <div className={DOT_GRID} />
    <div className="relative grid grid-cols-3 gap-2 max-w-[420px]">
      {["Curiosity", "Focus", "Empathy", "Logic", "Motor", "Language"].map((t, i) => (
        <div
          key={t}
          className={`px-2.5 py-2 rounded-lg text-[11.5px] border ${
            i === 1 || i === 4
              ? "bg-neutral-900 dark:bg-white text-white dark:text-neutral-900 border-neutral-900 dark:border-white"
              : "bg-white dark:bg-neutral-900 text-neutral-700 dark:text-neutral-300 border-neutral-200 dark:border-neutral-800"
          }`}
        >
          {t}
        </div>
      ))}
    </div>
    <div className="relative mt-4 max-w-[420px] p-3 rounded-lg bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800">
      <div className="text-[10.5px] uppercase tracking-widest text-neutral-400 dark:text-neutral-500">Today's activity</div>
      <div className="mt-1 text-[13px] font-medium text-neutral-900 dark:text-neutral-50">Story building with shapes</div>
      <div className="mt-1 text-[11.5px] text-neutral-500 dark:text-neutral-400">12 min · Focus + Language</div>
    </div>
  </div>
);

const SatvikScanVisual = () => (
  <div className={VISUAL_FRAME}>
    <div className={DOT_GRID} />
    <div className="relative max-w-[420px] p-4 rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800">
      <div className="flex items-center justify-between">
        <div className="text-[12px] font-medium text-neutral-900 dark:text-neutral-50">Cosmetic cream · scan</div>
        <div className="text-[11px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200 dark:bg-amber-500/15 dark:text-amber-300 dark:border-amber-500/30">Caution</div>
      </div>
      <div className="mt-3 space-y-1.5">
        {[
          { k: "Parabens", s: "high" },
          { k: "Fragrance mix", s: "med" },
          { k: "Niacinamide", s: "safe" },
        ].map((r) => (
          <div key={r.k} className="flex items-center justify-between text-[12px]">
            <span className="text-neutral-700 dark:text-neutral-300">{r.k}</span>
            <div className="flex items-center gap-1.5">
              <span className={`w-1.5 h-1.5 rounded-full ${SEVERITY_DOT[r.s] || "bg-neutral-400"}`} />
              <span className="text-neutral-500 dark:text-neutral-400 uppercase tracking-wider text-[10px]">{r.s}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-3 pt-3 border-t border-neutral-100 dark:border-neutral-800 text-[11px] text-neutral-500 dark:text-neutral-400">
        Why this verdict? Tap to see sources →
      </div>
    </div>
  </div>
);

const AngelProVisual = () => (
  <div className={VISUAL_FRAME}>
    <div className={DOT_GRID} />
    <div className="relative max-w-[420px] p-4 rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800">
      <div className="flex items-center justify-between">
        <div className="text-[11.5px] uppercase tracking-wider text-neutral-400 dark:text-neutral-500">Decision 2 of 3</div>
        <div className="text-[11.5px] text-neutral-500 dark:text-neutral-400">~40s left</div>
      </div>
      <div className="mt-2 text-[14px] font-medium text-neutral-900 dark:text-neutral-50">How long will you stay invested?</div>
      <div className="mt-3 grid grid-cols-3 gap-2">
        {["< 1 yr", "1–5 yrs", "5+ yrs"].map((t, i) => (
          <div
            key={t}
            className={`px-2 py-2 rounded-lg text-[12px] text-center border ${
              i === 1
                ? "bg-neutral-900 dark:bg-white text-white dark:text-neutral-900 border-neutral-900 dark:border-white"
                : "bg-white dark:bg-neutral-900 text-neutral-700 dark:text-neutral-300 border-neutral-200 dark:border-neutral-800"
            }`}
          >
            {t}
          </div>
        ))}
      </div>
      <div className="mt-3 h-1.5 w-full rounded-full bg-neutral-100 dark:bg-neutral-800 overflow-hidden">
        <div className="h-full w-2/3 bg-neutral-900 dark:bg-neutral-200" />
      </div>
    </div>
  </div>
);

const QiHealthFallbackVisual = () => (
  <div className={VISUAL_FRAME}>
    <div className={DOT_GRID} />
    <div className="relative grid grid-cols-2 gap-2 max-w-[440px]">
      {[
        { r: "Doctor", c: "Cases · 14" },
        { r: "Nurse", c: "Tasks · 27" },
        { r: "Admin", c: "Beds · 82%" },
        { r: "Patient", c: "Visits · 3" },
      ].map((p, i) => (
        <div
          key={p.r}
          className={`p-2.5 rounded-lg border ${
            i === 0
              ? "bg-neutral-900 dark:bg-white text-white dark:text-neutral-900 border-neutral-900 dark:border-white"
              : "bg-white dark:bg-neutral-900 border-neutral-200 dark:border-neutral-800"
          }`}
        >
          <div className="text-[10.5px] uppercase tracking-widest text-neutral-400 dark:text-neutral-500">
            {p.r}
          </div>
          <div className={`mt-0.5 text-[12.5px] ${i === 0 ? "text-white dark:text-neutral-900" : "text-neutral-800 dark:text-neutral-200"}`}>
            {p.c}
          </div>
        </div>
      ))}
    </div>
  </div>
);

// Lookup table — adding a new bespoke visual is a one-line change here.
const VISUAL_BY_ID = {
  sparkminds: SparkMindsVisual,
  satvikscan: SatvikScanVisual,
  angelpro: AngelProVisual,
};

const CaseStudyVisual = ({ cs }) => {
  // 1. Real photo when a case study has an image asset
  if (cs.image) {
    return (
      <ImageVisual
        image={cs.image}
        name={cs.name}
        imagePosition={cs.imagePosition}
      />
    );
  }
  // 2. Bespoke per-case visual
  const Visual = VISUAL_BY_ID[cs.id];
  if (Visual) return <Visual />;
  // 3. Fallback (e.g. Qi Health and any new case without a custom visual)
  return <QiHealthFallbackVisual />;
};

export default CaseStudies;
